import { useCallback, useEffect, useRef, useState } from 'react';

import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Spinner,
  Z,
} from '@alamo/ui';

import {
  appEntryFromDocument,
  createAppVersionMonitor,
  reloadWithCurrentWorker,
} from '../lib/appVersion';
import { consumePendingChunkStale } from '../lib/chunkReload';
import { clearStaleBundleAttempts } from '../lib/staleBundleRecovery';
import { waitForWaitingWorker } from '../lib/swUpdateChoreography';
import { router } from '../routes';

const UPDATE_CHECK_INTERVAL_MS = 60_000;
const NEW_SW_WAIT_TIMEOUT_MS = 7_000;
const HEALTHY_BOOT_MS = 15_000;

/** Release notifications preserve the current page until the user chooses to update. */
export function PwaUpdatePrompt(): JSX.Element | null {
  const { t } = useTranslation(['ui']);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const notifiedRef = useRef(false);
  const releaseCheckRef = useRef<() => Promise<boolean | null>>(async () => null);
  const updateControllerRef = useRef<AbortController | null>(null);
  const checkingRef = useRef<() => Promise<void>>(async () => undefined);
  const [updateRequired, setUpdateRequired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const requireUpdate = useCallback(() => {
    if (notifiedRef.current) return;
    notifiedRef.current = true;
    setUpdateRequired(true);
  }, []);

  const requireWorkerUpdate = useCallback(() => {
    // A controller change can follow a navigation that already loaded the new entry.
    // Only an outdated document needs another update notification.
    void releaseCheckRef.current().then(
      (current) => {
        if (current !== true) requireUpdate();
      },
      () => requireUpdate(),
    );
  }, [requireUpdate]);

  const {
    needRefresh: [needRefresh],
  } = useRegisterSW({
    immediate: true,
    // Workbox also invokes this when another tab activates a waiting worker.
    // Only the explicit update action below may reload this page.
    onNeedReload: requireWorkerUpdate,
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? null;
      if (registration?.waiting) requireWorkerUpdate();
      void checkingRef.current();
    },
  });

  useEffect(() => {
    const monitor = createAppVersionMonitor({
      entry: appEntryFromDocument(document, window.location.origin),
      origin: window.location.origin,
      onUpdateRequired: requireUpdate,
    });
    releaseCheckRef.current = monitor.check;
    const check = async () => {
      await monitor.check();
      await registrationRef.current?.update().catch(() => undefined);
      if (registrationRef.current?.waiting) requireWorkerUpdate();
    };
    checkingRef.current = check;
    let controller = navigator.serviceWorker?.controller ?? null;
    const onControllerChange = () => {
      const next = navigator.serviceWorker?.controller ?? null;
      // The first installation has no old application to replace.
      if (controller && next !== controller) requireWorkerUpdate();
      controller = next;
    };
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const checkOnFocus = () => {
      void check();
    };
    navigator.serviceWorker?.addEventListener('controllerchange', onControllerChange);
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkOnFocus);
    window.addEventListener('online', checkOnFocus);
    let lastPath = router.state.location.pathname;
    const unsubscribeRouter = router.subscribe((state) => {
      if (state.location.pathname !== lastPath) {
        lastPath = state.location.pathname;
        void check();
      }
    });
    const interval = setInterval(checkOnFocus, UPDATE_CHECK_INTERVAL_MS);
    const healthyBoot = setTimeout(clearStaleBundleAttempts, HEALTHY_BOOT_MS);
    void check();
    return () => {
      updateControllerRef.current?.abort();
      checkingRef.current = async () => undefined;
      releaseCheckRef.current = async () => null;
      navigator.serviceWorker?.removeEventListener('controllerchange', onControllerChange);
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkOnFocus);
      window.removeEventListener('online', checkOnFocus);
      unsubscribeRouter();
      clearInterval(interval);
      clearTimeout(healthyBoot);
    };
  }, [requireUpdate, requireWorkerUpdate]);

  useEffect(() => {
    if (needRefresh) requireWorkerUpdate();
  }, [needRefresh, requireWorkerUpdate]);

  useEffect(() => {
    if (consumePendingChunkStale()) requireUpdate();
    window.addEventListener('alamo:chunk-stale', requireUpdate);
    return () => window.removeEventListener('alamo:chunk-stale', requireUpdate);
  }, [requireUpdate]);

  async function update(): Promise<void> {
    if (updateControllerRef.current) return;
    const controller = new AbortController();
    updateControllerRef.current = controller;
    setBusy(true);
    setFailed(false);
    try {
      const registration = registrationRef.current;
      if (registration) {
        await waitForWaitingWorker(registration, NEW_SW_WAIT_TIMEOUT_MS, controller.signal);
      }
      await reloadWithCurrentWorker(
        registration,
        navigator.serviceWorker,
        () => window.location.reload(),
        controller.signal,
      );
    } catch {
      if (!controller.signal.aborted) setFailed(true);
    } finally {
      if (updateControllerRef.current === controller) {
        updateControllerRef.current = null;
        setBusy(false);
      }
    }
  }

  function dismiss(): void {
    updateControllerRef.current?.abort();
    updateControllerRef.current = null;
    setBusy(false);
    setUpdateRequired(false);
  }

  if (!updateRequired) return null;
  return (
    <AlertDialog open onOpenChange={(open) => !open && dismiss()}>
      <AlertDialogContent zIndex={Z.TOAST}>
        <AlertDialogHeader>
          <RefreshCw className="mb-2 size-6 text-laranja-encanto" aria-hidden />
          <AlertDialogTitle>{t('ui:pwa.update_available')}</AlertDialogTitle>
          <AlertDialogDescription>{t('ui:pwa.update_required')}</AlertDialogDescription>
        </AlertDialogHeader>
        <p className="mt-3 text-sm text-slate-600">{t('ui:pwa.unsaved_warning')}</p>
        {failed && (
          <p role="alert" className="mt-3 text-sm text-error-500">
            {t('ui:pwa.update_failed')}
          </p>
        )}
        <AlertDialogFooter className="flex-col-reverse sm:flex-row">
          <AlertDialogCancel>
            <Button variant="outline" className="w-full sm:w-auto">
              {t('ui:pwa.update_later')}
            </Button>
          </AlertDialogCancel>
          <Button onClick={() => void update()} disabled={busy} className="w-full sm:w-auto">
            {busy ? (
              <>
                <Spinner size="sm" />
                {t('ui:pwa.updating')}
              </>
            ) : (
              t('ui:pwa.update_action')
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
